<template>
  <ChatModal name="new-chat" :title="$t('chat.createGroupTitle')" @before-open="beforeOpen">
    <template #default>
      <div :class="$style.content">
        <template v-if="step === 0">
          <SearchInput :value="search" :class="$style.searchInput" @input="onInput" @clear="resetQuery" />
          <div :class="$style.section">
            <template v-if="globalSearchUsers.length">
              <div :class="$style.sectionHeader">
                <p :class="$style.sectionTitle">{{ $t('search.globalSearch') }}</p>
                <p
                  v-if="loadMoreGlobalSearch && availableUsers.length > 3"
                  @click="loadMoreGlobalSearch = false"
                  :class="$style.sectionMore"
                >
                  {{ $t('search.loadMore') }}
                </p>
              </div>
              <UserList
                :users="globalSearchUsers"
                :class="$style.userList"
                mode="select"
                :selectedUsers.sync="selectedUsers"
                :infiniteHandler="findAvailableUsers"
              />
            </template>
            <template v-if="users.length">
              <div :class="$style.sectionHeader">
                <p :class="$style.sectionTitle">{{ $t('search.myContacts') }}</p>
                <p
                  v-if="loadMoreMyContacts && allUsers.length > 3"
                  @click="loadMoreMyContacts = false"
                  :class="$style.sectionMore"
                >
                  {{ $t('search.loadMore') }}
                </p>
              </div>
              <UserList
                :users="users"
                :class="$style.userList"
                mode="select"
                :selectedUsers.sync="selectedUsers"
                :infiniteHandler="infiniteHandler"
              />
            </template>
            <div v-if="isNotFound" :class="$style.empty">
              <p :class="$style.txt">{{ $t('search.notFound') }}</p>
            </div>
          </div>
        </template>
        <template v-else>
          <div :class="$style.croppaWrap">
            <croppa
              v-model="croppa"
              :width="220"
              :height="220"
              :show-remove-button="true"
              remove-button-color="black"
              :remove-button-size="30"
              :prevent-white-space="true"
            />
          </div>
          <BaseInput
            :label="$t('chat.name')"
            v-model="chatName"
            :class="$style.input"
            :invalid="$v.chatName.$error"
            :error="nameError"
          />
          <BaseInput
            :label="$t('chat.description')"
            multiline
            rows="3"
            v-model="chatDescription"
            :class="$style.input"
            :invalid="$v.chatDescription.$error"
            :error="descriptionError"
          />
        </template>
      </div>
    </template>

    <template #footer>
      <BaseButton v-if="step === 0" oval :disabled="!selectedUsers.length" @click="next">{{
        $t('buttonNext')
      }}</BaseButton>
      <template v-else>
        <BaseButton oval secondary @click="back">{{ $t('buttonBack') }}</BaseButton>
        <BaseButton oval @click="onSubmit" :loading="loading">{{ $t('buttonCreate') }}</BaseButton>
      </template>
    </template>
  </ChatModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import ChatModal from './ChatModal'
import UserList from '@/components/UserList'
import SearchInput from '@/components/SearchInput'
import { TYPE_CODE, NAME_MAX_LENGTH, DESCRIPTION_MIN_LENGTH, DESCRIPTION_MAX_LENGTH } from '@/models/Chat'
import { debounce } from 'lodash'
import api from '@/api'

const LIMIT = 40

export default {
  name: 'new-chat-popup',
  components: {
    ChatModal,
    UserList,
    SearchInput
  },
  data () {
    return {
      loadMoreGlobalSearch: true,
      loadMoreMyContacts: true,
      step: 0,
      selectedUsers: [],
      availableUsers: [],
      chatName: '',
      chatDescription: '',
      croppa: null,
      loading: false,
      search: ''
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      contacts: 'entities/users/contacts',
      searchUsers: 'entities/users/search'
    }),
    searchQuery () {
      const search = this.search.trim()
      if (search.length < 3) return ''
      return search
    },
    allUsers () {
      return this.searchQuery ? this.searchUsers(this.searchQuery) : this.contacts
    },
    users () {
      return this.loadMoreMyContacts ? this.allUsers.slice(0, 3) : this.allUsers
    },
    globalSearchUsers () {
      return this.loadMoreGlobalSearch ? this.filteredAvailableUsers.slice(0, 3) : this.filteredAvailableUsers
    },
    filteredAvailableUsers () {
      return this.availableUsers?.filter(e => !(e?.nickname?.includes('wa:') || e?.nickname?.includes('tg:'))) || []
    },
    nameError () {
      if (!this.$v.chatName.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${NAME_MAX_LENGTH} ${this.$t('errorLength_end')}`
      }
      return this.$t('errorEmptyField')
    },
    descriptionError () {
      if (!this.$v.chatDescription.minLength) {
        return `${this.$t('errorMinLength_begin')} ${DESCRIPTION_MIN_LENGTH} ${this.$t('errorLength_end')}`
      }
      return `${this.$t('errorMaxLength_begin')} ${DESCRIPTION_MAX_LENGTH} ${this.$t('errorLength_end')}`
    },
    page () {
      return parseInt(this.contacts.length / LIMIT) + 1
    },
    isNotFound () {
      return !this.globalSearchUsers.length && !this.users.length
    }
  },
  watch: {
    searchQuery (newValue, oldValue) {
      if (!newValue || newValue !== oldValue) {
        this.availableUsers = []
      }
      this.findAvailableUsers()
      this.loadMoreGlobalSearch = true
      this.loadMoreMyContacts = true
    }
  },
  validations: {
    chatName: {
      required,
      maxLength: maxLength(NAME_MAX_LENGTH)
    },
    chatDescription: {
      required,
      minLength: minLength(DESCRIPTION_MIN_LENGTH),
      maxLength: maxLength(DESCRIPTION_MAX_LENGTH)
    }
  },
  methods: {
    beforeOpen () {
      this.loading = false
      this.selectedUsers = []
      this.chatName = ''
      this.chatDescription = ''
      this.step = 0
      this.croppa = null
      this.$v.$reset()
      this.resetQuery()
    },
    resetQuery () {
      this.search = ''
    },
    next () {
      if (!this.selectedUsers.length) return
      this.step = 1
    },
    back () {
      this.step = 0
    },
    ...mapActions({
      createChat: 'entities/chats/create',
      fetchContactsUsers: 'entities/users/fetchContactsUsers'
    }),
    onInput: debounce(async function (query) {
      this.search = query
    }, 800),
    async onSubmit () {
      this.$v.$touch()
      if (this.loading || this.$v.$invalid) return
      this.loading = true
      // try {
      const params = {
        type: TYPE_CODE.group,
        name: this.chatName,
        description: this.chatDescription,
        participants: this.selectedUsers
      }
      if (this.croppa.chosenFile) {
        const blob = await this.croppa.promisedBlob('image/jpeg', 0.8)
        params.photo = new File([blob], 'chat_photo.jpeg')
      }
      const { id } = await this.createChat(params)
      this.$router.push({ name: 'app-chat', query: { id: window.btoa(id) } })
      // this.$emit('go-to-chat', chat)
      this.$modal.hide('new-chat')
      // } catch (error) {
      // console.log(error)
      // this.$modal.show('error', { error: error.message })
      // } finally {
      this.loading = false
      // }
    },
    async infiniteHandler ($state) {
      const data = await this.fetchContactsUsers({
        page: this.page + 1,
        limit: LIMIT
      })
      if (data.length && data.length === LIMIT) {
        $state.loaded()
      } else {
        $state.complete()
      }
    },
    findAvailableUsers ($state) {
      if (
        !this.searchQuery ||
        (this.availableUsers.length > 0 && this.availableUsers.length < this.currentPageOfAvailableContacts * LIMIT)
      ) {
        return $state?.complete()
      }

      const nextPage =
        this.availableUsers.length === 0 ? this.currentPageOfAvailableContacts : this.currentPageOfAvailableContacts + 1
      const params = { value: this.searchQuery, page: nextPage, limit: LIMIT }
      api.user.publicSearch({ params }).then(({ data }) => {
        this.availableUsers.push(...data)
        if (data.length === LIMIT) {
          $state?.loaded()
        } else {
          $state?.complete()
        }
      })
    }
  }
}
</script>

<style lang="scss" module>
.input {
  margin-top: 14px;
}
.input,
.list :global(.gl-user-list-item) {
  padding-left: 15px;
  padding-right: 15px;
}
.croppaWrap {
  text-align: center;
}
.content {
  max-height: calc(100vh - 200px);
  height: 570px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.section {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  &Header {
    align-items: flex-end;
    border-bottom: 1px solid $border-color;
    display: flex;
    justify-content: space-between;
    padding: 0 15px;
    height: 40px;
  }
  &Title {
    color: $text-colored;
    font-size: 18px;
    font-weight: 500;
    line-height: 30px;
    margin-top: 10px;
  }
  &More {
    color: $text-dark;
    cursor: pointer;
    font-size: 16px;
    font-weight: 400;
    line-height: 40px;
    &:hover {
      color: $black;
    }
  }
}
.userList {
  & > li {
    border-bottom: 1px solid $border-color;
    padding-left: 15px;
    padding-right: 15px;
  }
}
.empty {
  align-items: center;
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: center;
}
</style>

<style lang="scss" scoped>
::v-deep {
  .infinite-loading-container {
    display: none;
  }
}
</style>
